import React, { useMemo } from 'react'
import Modal from '../Modal/Modal'
import { useNavigate, useParams } from 'react-router'
import { useGetLinkedAccounts } from '../../api/agencies/useGetLinkedAccounts'
import { getImageURL } from '../../lib/utils/getImageURL'
import dayjs from 'dayjs'
import { Button, Toast } from '@demandlocal/components'
import { useDeleteLinkedAccount } from '../../api/agencies/useDeleteLinkedAccount'
import toast from 'react-hot-toast'
import { match } from 'ts-pattern'
import { VENDOR } from '../../lib/utils/vendors'

export default function DeleteVendorAccount(): JSX.Element {
  const { account_id } = useParams()
  const navigate = useNavigate()
  const { data: vendors } = useGetLinkedAccounts()

  const vendorInfo = useMemo(
    () => vendors?.find((v) => v.id === Number(account_id)),
    [vendors, account_id],
  )

  const { mutate: mutateAsync } = useDeleteLinkedAccount()

  const warningMessage = useMemo(() => {
    return match(vendorInfo?.id)
      .with(VENDOR.Google, () => {
        return (
          <Toast
            className="w-fit"
            variant="error"
            text="Warning: Un-linking your account will delete your self service campaigns from our platform and you will not be able to push custom audiences to Google."
          />
        )
      })
      .with(VENDOR.GoogleAnalytics, () => {
        return (
          <Toast
            className="w-fit"
            variant="error"
            text="Warning: Un-linking your account will delete all your GA4 data from our platform. If re-connected, we will only be able to retrieve 30 days of history"
          />
        )
      })
      .otherwise(() => null)
  }, [vendorInfo])

  return (
    <Modal key="DeleteVendorAccount" isOpen modalRoute>
      <div className="flex flex-col h-full w-full p-10 gap-5">
        <h1 className="font-bold text-xl  mb-5">
          Are you sure you want to delete this linked account?
        </h1>
        {warningMessage}
        <div className="flex flex-col gap-5 divide-y-2 my-auto">
          <div className="flex flex-row gap-5">
            <div className="flex justify-center items-center">
              {vendorInfo?.logo ? (
                <img
                  src={getImageURL(`images/${vendorInfo.logo}`)}
                  className="h-10 w-10 object-contain"
                />
              ) : (
                <></>
              )}
            </div>
            <div className="flex flex-col gap-2 items-start justify-center">
              <span className="font-bold text-lg">{vendorInfo?.name} Account</span>
              <span>
                Connected as{' '}
                <span className="font-bold">
                  {vendorInfo?.connectionDetails?.user_name}
                </span>{' '}
                on{' '}
                {dayjs(vendorInfo?.connectionDetails?.created_date).format('MM/DD/YYYY')}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-3 py-5">
            <span className="font-bold">Customers Connected</span>
            <span>N/A</span>
          </div>
          <div className="flex flex-col gap-3 py-5">
            <span className="font-bold">Campaigns linked to this account</span>
            <span>N/A</span>
          </div>
        </div>
        <div className="flex flex-row justify-between py-5 border-t-2">
          <Button
            variant="tertiary"
            onClick={(e) => {
              e.preventDefault()
              navigate('../')
            }}>
            Cancel
          </Button>
          <Button
            variant="rainbow"
            onClick={async (e) => {
              e.preventDefault()
              const toastId = toast.loading('Deleting Linked Account')
              try {
                const res = await mutateAsync({ account_id: Number(account_id) })

                toast.success('Successfully deleted linked account', {
                  id: toastId,
                  duration: 5000,
                })
                navigate('../')
              } catch (err) {
                toast.error('Error deleting linked account', {
                  id: toastId,
                  duration: 5000,
                })
              }
            }}>
            Yes, delete it
          </Button>
        </div>
      </div>
    </Modal>
  )
}
